import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import * as api from '../services/api';
import toast from 'react-hot-toast';
import { generateDeviceFingerprint } from '../utils/deviceFingerprint';

const CreditContext = createContext(null);

// Cache time-to-live constants
const ITEMS_TTL      = 5  * 60 * 1000; // 5 minutes
const CREDITS_TTL    = 2  * 60 * 1000; // 2 minutes
const PROFILE_TTL    = 10 * 60 * 1000; // 10 minutes — profile changes rarely
const PAY_HISTORY_TTL = 10 * 60 * 1000; // 10 minutes — payment history rarely changes

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export function CreditProvider({ children }) {
  const { user, token } = useAuth();

  const [credits, setCredits]         = useState(0);
  const [plan, setPlan]               = useState('free');
  const [tokens, setTokens]           = useState(0);
  const [profile, setProfile]         = useState(null);
  const [items, setItems]             = useState([]);
  const [pendingJobs, setPendingJobs] = useState([]);
  const [payHistory, setPayHistory]   = useState([]);

  // Cache timestamps — reset on UID change, not on token rotation
  const lastItemsFetch      = useRef(0);
  const lastCreditsFetch    = useRef(0);
  const lastProfileFetch    = useRef(0);
  const lastPayHistoryFetch = useRef(0);

  // In-flight deduplication flags
  const itemsFetching      = useRef(false);
  const creditsFetching    = useRef(false);
  const profileFetching    = useRef(false);
  const payHistoryFetching = useRef(false);

  // Track the last UID we bootstrapped for — CRITICAL: prevents re-fetching on token rotation
  const lastBootstrappedUid = useRef(null);

  // Welcome claim: only once per UID per session
  const welcomeClaimedUids = useRef(new Set());

  // Always-current token ref so callbacks don't go stale on token rotation
  const tokenRef = useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);

  // ── Credits ──────────────────────────────────────────────────────────────
  const refresh = useCallback(async (force = false) => {
    const tok = tokenRef.current;
    if (!tok) return;
    const now = Date.now();
    if (!force && (now - lastCreditsFetch.current < CREDITS_TTL)) return;
    if (creditsFetching.current) return;
    creditsFetching.current = true;
    try {
      const data = await api.fetchCredits(tok);
      setCredits(data.credits ?? 0);
      setPlan(data.plan || 'free');
      setTokens(data.tokens ?? 0);
      lastCreditsFetch.current = Date.now();
    } catch {}
    finally { creditsFetching.current = false; }
  }, []); // No token dependency — reads tokenRef instead

  // ── Profile ──────────────────────────────────────────────────────────────
  const refreshProfile = useCallback(async (force = false) => {
    const tok = tokenRef.current;
    if (!tok) return;
    const now = Date.now();
    if (!force && (now - lastProfileFetch.current < PROFILE_TTL)) return;
    if (profileFetching.current) return;
    profileFetching.current = true;
    try {
      const data = await api.fetchProfile(tok);
      setProfile(data);
      lastProfileFetch.current = Date.now();
    } catch {}
    finally { profileFetching.current = false; }
  }, []); // No token dependency — reads tokenRef instead

  // ── Generations ──────────────────────────────────────────────────────────
  const refreshItems = useCallback(async (force = false) => {
    const tok = tokenRef.current;
    if (!tok) return;
    const now = Date.now();
    if (!force && (now - lastItemsFetch.current < ITEMS_TTL)) return;
    if (itemsFetching.current) return;
    itemsFetching.current = true;
    try {
      const data = await api.fetchGenerations(tok);
      setItems(data);
      lastItemsFetch.current = Date.now();
    } catch {}
    finally { itemsFetching.current = false; }
  }, []); // No token dependency — reads tokenRef instead

  // ── Payment History ───────────────────────────────────────────────────────
  const refreshPayHistory = useCallback(async (force = false) => {
    const tok = tokenRef.current;
    if (!tok) return;
    const now = Date.now();
    if (!force && (now - lastPayHistoryFetch.current < PAY_HISTORY_TTL)) return;
    if (payHistoryFetching.current) return;
    payHistoryFetching.current = true;
    try {
      const data = await api.fetchPaymentHistory(tok);
      setPayHistory(data);
      lastPayHistoryFetch.current = Date.now();
    } catch {}
    finally { payHistoryFetching.current = false; }
  }, []); // No token dependency — reads tokenRef instead

  // ── Optimistic update ─────────────────────────────────────────────────────
  const addItem = useCallback((item) => {
    setItems(prev => [item, ...prev]);
  }, []);

  // ── Delete helper ─────────────────────────────────────────────────────────
  const removeItem = useCallback((id) => {
    setItems(prev => prev.filter(i => i.$id !== id && i.id !== id));
  }, []);

  // ── Bootstrap on USER CHANGE ONLY (not token rotation) ───────────────────
  // We key off user.uid, NOT token. Firebase rotates the token every ~55 min
  // but the UID never changes during a session — so this only fires on actual
  // login / logout / account switch, not on background token refreshes.
  const uid = user?.uid ?? null;

  useEffect(() => {
    if (uid && token) {
      // Skip if we already bootstrapped for this UID this session
      if (lastBootstrappedUid.current === uid) return;
      lastBootstrappedUid.current = uid;

      // Reset cache so we always load fresh data for the newly logged-in user
      lastCreditsFetch.current = 0;
      lastItemsFetch.current   = 0;
      lastProfileFetch.current = 0;

      // Load all data for this user
      refresh(true);
      refreshProfile(true);
      refreshItems(true);
      refreshPayHistory(true);

      // ── Welcome credit claim (device-gated, once per UID per session) ─────
      if (!welcomeClaimedUids.current.has(uid)) {
        welcomeClaimedUids.current.add(uid);
        setTimeout(async () => {
          try {
            const tok = tokenRef.current;
            if (!tok) return;
            const deviceFingerprint = await generateDeviceFingerprint();
            const res = await fetch(`${API_BASE}/user/claim-welcome`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${tok}`,
              },
              body: JSON.stringify({ deviceFingerprint }),
            });
            const data = await res.json();
            if (data.success && !data.alreadyClaimed && data.granted > 0) {
              setCredits(data.credits);
              toast.success(`🎉 Welcome! You've received ${data.granted} free credits to get started.`, {
                duration: 6000,
                style: {
                  background: '#1a1a2e',
                  color: '#e0d4ff',
                  border: '1px solid rgba(139,92,246,0.4)',
                  fontWeight: 600,
                },
              });
            }
          } catch (e) {
            console.warn('[WelcomeCredits]', e.message);
          }
        }, 2500);
      }
    } else if (!uid) {
      // Actual logout — clear everything and reset bootstrap tracker
      lastBootstrappedUid.current = null;
      setCredits(0);
      setPlan('free');
      setTokens(0);
      setProfile(null);
      setItems([]);
      setPendingJobs([]);
      setPayHistory([]);
      lastItemsFetch.current      = 0;
      lastCreditsFetch.current    = 0;
      lastProfileFetch.current    = 0;
      lastPayHistoryFetch.current = 0;
    }
  }, [uid, token]); // token included so the first run has a valid token

  return (
    <CreditContext.Provider value={{
      credits, setCredits,
      plan, setPlan,
      tokens, setTokens,
      profile, setProfile,
      items, setItems,
      pendingJobs, setPendingJobs,
      payHistory, setPayHistory,
      refresh, refreshProfile, refreshItems, refreshPayHistory,
      addItem, removeItem,
    }}>
      {children}
    </CreditContext.Provider>
  );
}

export function useCredits() {
  const ctx = useContext(CreditContext);
  if (!ctx) throw new Error('useCredits must be inside CreditProvider');
  return ctx;
}
